import { Agent, QueryParams } from "@/sharedTypes";
import { baseApi } from "../baseApi";
import { flattenQueryParams } from "@/utils/requestUtils";

const agentTag = "Agent";

export const agentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAgents: builder.query<any, Record<string, any> | void>({
      query: (params) => {
        const queryString = params
          ? `?${new URLSearchParams(params).toString()}`
          : "";
        return `/agents${queryString}`;
      },
      providesTags: [agentTag],
    }),
    getAgentById: builder.query<Agent, string>({
      query: (id) => `/agents/${id}`,
      transformResponse: (response: any) => response?.data ?? response,
      providesTags: (_result, _error, id) => [{ type: agentTag, id }],
    }),
    createAgent: builder.mutation<Agent, Partial<Agent>>({
        query: (agent) => ({
            url: `/agents`,
            method: 'POST',
            body: agent
        }),
        invalidatesTags: [agentTag]
    }),
    editAgent: builder.mutation<any, { agentId: string; updatedDetails: Partial<Agent> }>({
      query: ({ agentId, updatedDetails }) => ({
        url: `/agents/${agentId}`,
        method: "PUT",
        body: updatedDetails,
      }),
      invalidatesTags: [agentTag],
    }),
    deleteAgent: builder.mutation<void, string>({
      query: (agentId) => ({
        url: `/agents/${agentId}`,
        method: "DELETE",
      }),
      invalidatesTags: [agentTag],
    }),
    restoreAgent: builder.mutation<void, number>({
      query: (agentId) => ({
        url: `/agents/${agentId}/restore`,
        method: "PUT",
      }),
      invalidatesTags: [agentTag],
    }),
    hardDeleteAgent: builder.mutation<void, number>({
      query: (agentId) => ({
        url: `/agents/${agentId}/permanent`,
        method: "DELETE",
      }),
      invalidatesTags: [agentTag],
    }),
    getAgentGroups: builder.query<any, { agentId: string; params: QueryParams }>({
      query: ({ agentId, params }) => {
        const queryString = params
          ? `?${new URLSearchParams(flattenQueryParams(params)).toString()}`
          : "";
        return `/agentGroup/agent/${agentId}${queryString}`;
      },
      providesTags: [agentTag, "Group"],
    }),
    getCurrentAgent: builder.query<any, void>({
      query: () => `/agents/profile/me`,
      transformResponse: (response: any) => response?.data ?? response,
    }),
  }),
});

export const {
  useGetAgentsQuery,
  useGetAgentByIdQuery,
  useCreateAgentMutation,
  useEditAgentMutation,
  useDeleteAgentMutation,
  useRestoreAgentMutation,
  useHardDeleteAgentMutation,
  useGetAgentGroupsQuery,
  useGetCurrentAgentQuery,
} = agentApi;
